import moment from 'moment';
import {
  ReactNode,
  useContext,
  createContext,
  useState,
  Dispatch,
  useEffect,
  SetStateAction,
  useCallback,
  useMemo
} from 'react';
import { NFTDetails } from '@/Component/NFTCard';
import { RuntimeConfiguration } from '@/configuration';
import { FilterProps } from '@/graphQl/nftList.type';
import { isSaleActive, isSaleEnded } from '../utils/common.utils';
import {
  GET_BUYERS_LIST,
  GET_SOLD_ITEM_BY_TOKEN,
  GET_SOLD_ITEMS_WITH_CONTACT_GALLERIES,
  SOLD_ITEMS
} from '@/graphQl/nftList';
import { GraphQLClient } from 'graphql-request';
import { QueryObserverResult, RefetchOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getTokenCounts } from '@/services/tokens.service';

// Define the NftContent type that includes DropList and the setter function
export type NftSoldDetail = NFTDetails & { isSoldOut: boolean };

export type NftFilter = {
  offset: number;
  isLoading: boolean;
  filter: FilterProps;
  search?: string;
  startDate?: string;
  endDate?: string;
  isSaleStarted: boolean;
  isSaleEnded: boolean;
};

export interface ConversionData {
  getConversionRate: GetConversionRate;
}

export interface GetConversionRate {
  rate: number;
  base: string;
  currency: string;
}

export type IsSoldOutToken = {
  owner?: string;
  isSoldOut: boolean;
};

export interface SoldItemCountDetails {
  count: number;
  tokenId: string;
}

export type BuyExecuted = {
  tokenId: string;
};

interface SoldItemsCount {
  stats: SoldItemCountDetails;
}
interface SoldItemIdsCount {
  buyNowCreateds: SoldItemsCount[];
}

interface contactGalleryItem {
  saleId: string;
  tokenId: string;
}

interface SoldTokens {
  buyNowCreateds: BuyExecuted[];
  contactGalleries: contactGalleryItem[];
}

interface BuyerItem {
  buyer: string;
}

interface BuyerList {
  buyExecutions: BuyerItem[];
}

interface BuyersListResponse {
  buyNowCreateds: BuyerList[];
}

export type NftContent = {
  nftFilter: NftFilter;
  totalNfts: number;
  totalSold?: number;
  tokenIds?: string[];
  noOfItems?: number;
  selectedNft?: NftSoldDetail;
  isNftListLoading: boolean;
  contactGalleryItems: contactGalleryItem[];
  soldItems: SoldItemCountDetails[];
  soldItemsIsLoading: boolean;
  isCountLoading: boolean;
  setSelectedNft: Dispatch<SetStateAction<NftSoldDetail | undefined>>;
  setNftFilter: Dispatch<SetStateAction<NftFilter>>;
  updateSearchFilter: (search: string) => void;
  handleFilterValues: () => void;
  refetchSoldTokens: (options?: RefetchOptions) => Promise<
    QueryObserverResult<
      {
        mintedNftsArray: string[];
        contactGalleryItems: contactGalleryItem[];
      },
      Error
    >
  >;
  fetchSoldItemsCount: (tokenId?: string | undefined) => Promise<
    | {
        tokenId: string;
        count: number;
      }[]
    | undefined
  >;
  fetchBuyersList: (tokenId: string) => Promise<string[]>;
};

export type IsSoldToken = {
  isSoldOut: boolean;
  owner?: string;
};

// Define the type for the provider's props
export type NftContextProviderType = {
  children: ReactNode;
};

// Create the context with a default empty value
export const NftContentContext = createContext<NftContent | undefined>(undefined);

export const nftFilterValues: NftFilter = {
  offset: 0,
  isLoading: false,
  filter: FilterProps.ALL,
  search: '',
  startDate: undefined,
  endDate: undefined,
  isSaleEnded: false,
  isSaleStarted: false
};

export const NFT_LIMIT = 1;

// Create the provider component
export const NftContentProvider = ({ children }: NftContextProviderType) => {
  const [nftFilter, setNftFilter] = useState<NftFilter>(nftFilterValues);
  const [selectedNft, setSelectedNft] = useState<NftSoldDetail>();
  const [soldItems, setSoldItems] = useState<SoldItemCountDetails[]>();

  const clientService = useMemo(() => {
    return new GraphQLClient(RuntimeConfiguration.SUBGRAPH_TOKENS_API);
  }, []);

  const handleFilterValues = useCallback(() => {
    const startDate = moment.unix(+RuntimeConfiguration.START_DATE).format('YYYY-MM-DD HH:mm:ss');
    const endDate = moment.unix(+RuntimeConfiguration.END_DATE).format('YYYY-MM-DD HH:mm:ss');

    setNftFilter((prev) => ({
      ...prev,
      startDate: moment.unix(+RuntimeConfiguration.START_DATE).toISOString(),
      endDate: moment.unix(+RuntimeConfiguration.END_DATE).toISOString(),
      isSaleStarted: isSaleActive(startDate, endDate),
      isSaleEnded: isSaleEnded(endDate)
    }));
  }, []);

  const updateSearchFilter = useCallback((search: string) => {
    setNftFilter((prev) => ({
      ...prev,
      search,
      offset: 0 // Reset offset when search changes
    }));
  }, []);

  // Reset offset when filter changes
  useEffect(() => {
    setNftFilter((prev) => ({
      ...prev,
      offset: 0
    }));
  }, [nftFilter.filter]);

  const {
    data: tokenCounts,
    isLoading: isTokenCountsLoading,
    isFetching: isTokenCountsFetching
  } = useQuery({
    queryKey: ['token_counts'],
    queryFn: async () => await getTokenCounts()
  });

  const fetchBuyersList = useCallback(
    async (tokenId: string) => {
      try {
        const data = await clientService.request<BuyersListResponse>(GET_BUYERS_LIST, { tokenId });
        const buyers = Array.from(
          new Set((data?.buyNowCreateds ?? []).flatMap((item) => (item.buyExecutions ?? []).map((exec) => exec.buyer)))
        );
        return buyers || [];
      } catch (e) {
        throw e;
      }
    },
    [clientService]
  );

  const fetchSoldItemsCount = useCallback(
    async (tokenId?: string) => {
      try {
        let data;

        if (tokenId) {
          data = await clientService.request<SoldItemIdsCount>(GET_SOLD_ITEM_BY_TOKEN, { tokenId });
        } else {
          data = await clientService.request<SoldItemIdsCount>(SOLD_ITEMS);
        }
        const seen = new Set<string>();
        const unique: SoldItemCountDetails[] = [];

        for (const entry of data?.buyNowCreateds ?? []) {
          const { tokenId, count } = entry.stats;

          if (!seen.has(tokenId)) {
            seen.add(tokenId);
            unique.push({ tokenId, count });
          }
        }

        if (!tokenId) {
          setSoldItems(unique ?? []);
        }

        return unique || [];
      } catch (e) {
        throw e;
      }
    },
    [clientService]
  );

  const {
    data: soldItemsData,
    isLoading: isSoldItemsLoading,
    isFetching: isSoldItemsFetching,
    refetch: refetchSoldTokens
  } = useQuery({
    queryKey: ['sold_items'],
    queryFn: async () => {
      const data = await clientService.request<SoldTokens>(GET_SOLD_ITEMS_WITH_CONTACT_GALLERIES);
      const mintedNftsArray: string[] = (data?.buyNowCreateds ?? []).map((item) => item.tokenId);
      return { mintedNftsArray, contactGalleryItems: data?.contactGalleries ?? [] };
    },
    enabled: false
  });

  useEffect(() => {
    handleFilterValues();
  }, [handleFilterValues]);

  return (
    <NftContentContext.Provider
      value={{
        nftFilter,
        totalNfts: tokenCounts || 0,
        tokenIds: soldItemsData?.mintedNftsArray ?? [],
        totalSold: soldItemsData?.mintedNftsArray?.length || 0,
        selectedNft,
        soldItemsIsLoading: isSoldItemsLoading || isSoldItemsFetching,
        contactGalleryItems: soldItemsData?.contactGalleryItems ?? [],
        soldItems: soldItems ?? [],
        isNftListLoading: false,
        fetchSoldItemsCount,
        fetchBuyersList,
        setSelectedNft,
        isCountLoading: isTokenCountsLoading || isTokenCountsFetching,
        setNftFilter,
        updateSearchFilter,
        handleFilterValues,
        refetchSoldTokens
      }}
    >
      {children}
    </NftContentContext.Provider>
  );
};

// Custom hook to use the NftContentContext
export const useNftContent = (): NftContent => {
  const context = useContext(NftContentContext);

  if (!context) {
    throw new Error('useNftContext must be used within a NftContextProvider');
  }

  return context;
};
